import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../stores/authStore";
import { authAPI } from "../services/authAPI";
import { setNavigateFunction } from "../utils/navigation";

interface AuthContextType {
  login: (
    email: string,
    password: string,
    totpCode?: string
  ) => Promise<{ requires2fa?: boolean }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  const { login: storeLogin, logout: storeLogout } = useAuthStore();
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("accessToken");

      if (token) {
        try {
          const userData = await authAPI.getMe();
          useAuthStore.getState().updateUser(userData);
        } catch {
          localStorage.clear();
          storeLogout();
          if (window.location.pathname !== "/login") {
            navigate("/login");
          }
        }
      } else {
        storeLogout();
        if (window.location.pathname !== "/login") {
          navigate("/login");
        }
      }

      setIsLoading(false);
    };

    verifyToken();
  }, [navigate, storeLogout]);

  const login = async (email: string, password: string, totpCode?: string) => {
    const response = await authAPI.login({ email, password, totpCode });
    if ("requires2fa" in response && response.requires2fa) {
      return { requires2fa: true };
    }
    if (!("accessToken" in response) || !response.accessToken) {
      throw new Error("Login failed: no access token");
    }

    const { user, accessToken, refreshToken } = response;

    localStorage.setItem("accessToken", accessToken);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    // Drop previous account's cached lists (e.g. Super Admin → Branch Admin)
    queryClient.clear();
    storeLogin(user, accessToken, refreshToken);
    return {};
  };

  const logout = () => {
    authAPI.logout();
    queryClient.clear();
    storeLogout();
    if (window.location.pathname !== "/login") {
      navigate("/login");
    }
  };

  const refreshUser = async () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const userData = await authAPI.getMe();
        useAuthStore.getState().updateUser(userData);
      } catch {
        logout();
      }
    }
  };

  return (
    <AuthContext.Provider value={{ login, logout, refreshUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
